// Holds a `turbo-frame[spinner]` overlay for as long as the frame's latest
// request is in flight.
//
// Turbo's own `busy` is not enough: every request ends by clearing it, the one
// Turbo aborted for a newer request included. Which of the two lands first is
// down to microtask timing, and a `window.fetch` wrapper — PostHog's network
// capture in session replay, a browser extension — delays the aborted one past
// the newer one's start. The newer request then runs with no overlay: a search
// typed with pauses lost its loader for as long as the server took to answer.
//
// So the overlay also follows `data-loading`, which only the frame's latest
// request may clear. The CSS keeps `[busy]` beside it because a frame loading
// its `src` on the very first render starts before this module has loaded.
const latestRequests = new WeakMap()

document.addEventListener('turbo:before-fetch-request', ({ target, detail: { fetchOptions } }) => {
  const { headers, signal } = fetchOptions
  const frameId = headers['Turbo-Frame']
  // A hovered link prefetches with its frame's header, but nothing loads into it yet.
  if (!frameId || headers['X-Sec-Purpose'] === 'prefetch') return

  const frame = document.getElementById(frameId)
  if (!frame?.matches('turbo-frame[spinner]')) return

  const request = new AbortController()
  const finish = () => {
    request.abort()
    if (latestRequests.get(frame) !== request) return

    latestRequests.delete(frame)
    frame.removeAttribute('data-loading')
  }

  latestRequests.set(frame, request)
  frame.setAttribute('data-loading', '')

  // Listened on the target itself: a form its frame has re-rendered is detached,
  // and its events no longer reach the document.
  target.addEventListener('turbo:before-fetch-response', finish, { signal: request.signal })
  target.addEventListener('turbo:fetch-request-error', finish, { signal: request.signal })
  signal.addEventListener('abort', finish, { signal: request.signal })
})
